import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { useEntity, GROUP_VALUE } from "@/lib/entityStore";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading } from "@/components/ds/PageShell";
import { Stat } from "@/components/ds/Stat";

export default function Debtors() {
    const { selectedEntityId } = useEntity();
    const [data, setData] = useState(null);

    useEffect(() => {
        const q = selectedEntityId === GROUP_VALUE ? "" : `?entity_id=${selectedEntityId}`;
        api.get(`/debtors${q}`).then((r) => setData(r.data));
    }, [selectedEntityId]);

    if (!data)
        return (
            <PageShell>
                <Loading label="Loading debtors…" testId="debtors-loading" />
            </PageShell>
        );

    const total = data.debtors.reduce((a, b) => a + b.outstanding, 0);
    const top = data.debtors[0];
    const topPct = top ? Math.round((top.outstanding / Math.max(1, total)) * 100) : 0;

    return (
        <PageShell width="default" testId="debtors-page">
            <PageHeader
                eyebrow="Receivables"
                title="Debtors"
                subtitle="Customers carrying outstanding invoices, ranked by exposure. Cash sitting with customers is cash not in the business."
            />

            <Card variant="default" padding="lg" className="mb-6" testId="debtors-summary">
                <div className="label-soft mb-2 text-stone-500">Outstanding</div>
                <div className="text-display-md text-stone-900 leading-snug mb-2">
                    {top ? (
                        <>
                            <span className="font-mono-num">{fmtMoney(total)}</span> across {data.debtors.length} debtors.
                        </>
                    ) : (
                        "No outstanding debtors in scope."
                    )}
                </div>
                {top && (
                    <p className="text-body text-stone-500 max-w-prose">
                        <span className="text-stone-700 font-medium">{top.name}</span> carries{" "}
                        <span className="text-stone-700 font-medium">{topPct}%</span> of total exposure
                        at <span className="font-mono-num text-stone-700">{fmtMoney(top.outstanding)}</span>.
                        Call them this week.
                    </p>
                )}
                <div className="grid grid-cols-3 gap-x-8 gap-y-5 mt-6">
                    <Stat label="Total outstanding" value={total} size="sm" />
                    <Stat label="Top debtor" value={top?.outstanding || 0} size="sm" helper={top?.name} />
                    <Stat label="Open invoices" value={data.invoices.length} money={false} size="sm" />
                </div>
            </Card>

            <Section title="Who owes us the most" subtitle="Ranked by outstanding balance.">
                <Card variant="default" padding="none" className="overflow-hidden" testId="debtor-table">
                    <table className="w-full text-[14px]">
                        <thead>
                            <tr className="text-left border-b border-stone-100 bg-stone-50/40">
                                <th className="px-5 py-3 label-soft text-stone-500 font-medium w-12">#</th>
                                <th className="px-3 py-3 label-soft text-stone-500 font-medium">Customer</th>
                                <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Outstanding</th>
                                <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Open</th>
                                <th className="px-5 py-3 label-soft text-stone-500 font-medium text-right">Oldest due</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.debtors.map((d, i) => (
                                <tr key={i} className="border-b border-stone-100 last:border-0">
                                    <td className="px-5 py-3.5 text-stone-400 font-mono-num">{i + 1}</td>
                                    <td className="px-3 py-3.5 text-stone-800">{d.name}</td>
                                    <td className="px-3 py-3.5 font-mono-num text-right text-stone-800">{fmtMoney(d.outstanding)}</td>
                                    <td className="px-3 py-3.5 font-mono-num text-right text-stone-700">{d.count}</td>
                                    <td className="px-5 py-3.5 font-mono-num text-right text-stone-500">{fmtDate(d.oldest_due)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            </Section>
        </PageShell>
    );
}
